import type { TreeNode } from './types';
import type { ComponentProps } from 'react';
import type { ListChildComponentProps } from 'react-window';

import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { areEqual, VariableSizeList } from 'react-window';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore } from '@core/store';
import { isBranch } from '@core/store/SuperHub/util';

import { RedirectPendingModalProvider } from '../Modals';

import styles from './index.module.scss';
import { BranchItem, EmptyBranchesItem, VersionItem, VERSION_ITEM_HEIGHT } from './Item';
import useTreeNodes from './useTreeNodes';

const List = forwardRef<HTMLDivElement>(function List({ ...rest }, ref) {
  const bem = useClassy(styles, 'VersionsList');

  return (
    <div
      {...rest}
      ref={ref}
      aria-label="Versions navigation tree"
      aria-orientation="vertical"
      className={bem()}
      role="tree"
    />
  );
});

const Item = React.memo(function Item({ data: treeNodes, index, style }: ListChildComponentProps<TreeNode[]>) {
  const node = treeNodes[index];

  switch (node.type) {
    case 'version':
      return <VersionItem node={node} style={style} />;
    case 'branch':
      return <BranchItem node={node} style={style} />;
    case 'empty_branches':
      return <EmptyBranchesItem style={style} />;
    default:
      return null;
  }
}, areEqual);

/**
 * Root element to render things like contexts around our list.
 */
const Container: React.FC<ComponentProps<'div'>> = props => {
  return (
    <RedirectPendingModalProvider>
      <div {...props} />
    </RedirectPendingModalProvider>
  );
};

export default function VersionsList({ className }: ComponentProps<'div'>) {
  const [currentVersionName, versions, versionsMap] = useSuperHubStore(s => [
    isBranch(s.currentBranch) ? s.currentBranch.base.name : s.currentBranch.name,
    s.versions.data.data,
    s.branches.versionsMap,
  ]);

  /**
   * Ref for the virtual list instance so we can access its API methods.
   * @link https://react-window.vercel.app/#/api/VariableSizeList
   */
  const listRef = useRef<VariableSizeList>(null);

  const {
    nodes: treeNodes,
    scrollTarget,
    selectedNode,
  } = useTreeNodes({
    selectedVersion: currentVersionName,
    versions,
    versionsMap,
  });

  // Whenever our tree changes when navigating between sections, we need to
  // reset the list to avoid rendering the new list with incorrect dimensions.
  useEffect(() => {
    if (treeNodes) {
      listRef.current?.resetAfterIndex(0, true);
    }
  }, [treeNodes]);

  // Run only on the initial mount b/c we only want to scroll to the selected
  // version once and never again.
  useEffect(() => {
    if (selectedNode) {
      window.requestAnimationFrame(() => {
        listRef.current?.scrollToItem(selectedNode.index, 'center');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a scroll target is set, scroll the virtual list to make it visible.
  // Wait for next animation frame to ensure list is rendered before executing.
  useEffect(() => {
    if (scrollTarget) {
      window.requestAnimationFrame(() => {
        listRef.current?.scrollToItem(scrollTarget.index, 'auto');
      });
    }
  }, [scrollTarget]);

  /** Calculates the total tree list height to be used during SSR. */
  const defaultListHeight = useMemo(() => {
    return treeNodes.reduce((height, node) => {
      return height + node.height;
    }, 0);
  }, [treeNodes]);

  const bem = useClassy(styles, 'VersionsList');

  return (
    <Container className={bem('-container', className)}>
      <AutoSizer defaultHeight={defaultListHeight}>
        {autoSizerProps => (
          <VariableSizeList
            ref={listRef}
            estimatedItemSize={VERSION_ITEM_HEIGHT}
            innerElementType={List}
            itemCount={treeNodes.length}
            itemData={treeNodes}
            itemKey={(index, nodes) => {
              const node = nodes[index];
              return 'data' in node ? node.data.uri : node.index;
            }}
            itemSize={index => treeNodes[index].height}
            overscanCount={5} // Tweak this to optimize empty flash scrolling.
            {...autoSizerProps}
          >
            {Item}
          </VariableSizeList>
        )}
      </AutoSizer>
    </Container>
  );
}
